<script setup lang="ts">
import { computed } from 'vue'
import { VAvatar } from '@/modules/shared/components'
import { rails_url } from '@/modules/shared/utils/rails'
import { transactionBadgeMap } from './transaction-display'

const props = defineProps<{
  value: string
  item: any
  group?: { items: any[] } | null
}>()

// In flat / subrow modes `item` is the raw row (carries
// portfolio_disbursement_type from the backend). In aggregate mode `item`
// is rawAggregates and only contains column-keyed values; reach the
// underlying row through `group.items[0]` instead.
const sourceRow = computed(() => props.group?.items?.[0] ?? props.item)

// When the row traces back to a portfolio disbursement (either the PDA row
// itself or a fund-side Disbursement distributing one onward), surface the
// original portfolio-company type — "Stock Sale Distribution", "Dividend
// Distribution", etc. — so the label is consistent end-to-end instead of
// flipping to the generic "Portfolio Distribution" / "Cash Distribution" /
// "Asset funds".
const label = computed(() => {
  const pdType = sourceRow.value?.portfolio_disbursement_type
  if (pdType) return `${pdType} Distribution`
  return transactionBadgeMap[props.value]?.label ?? props.value
})
</script>

<template>
  <div class="flex items-center gap-3">
    <span class="font-medium">
      {{ label }}
    </span>
    <span v-if="item.company" class="flex items-center gap-1.5">
      <span class="text-xs text-gray-500">
        {{ value === 'loss_investment' ? '→ to investors associated with' : 'from' }}
      </span>
      <a
        :href="`${rails_url()}/companies/${item.company.id}/disbursements`"
        :title="item.company.name"
        class="flex max-w-48 items-center gap-1.5 truncate rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700 transition-colors hover:bg-gray-50"
      >
        <VAvatar
          :image="item.company.logo_url ? decodeURIComponent(item.company.logo_url) : null"
          :initials="item.company.initials"
          size="xs"
        />
        <span class="truncate">{{ item.company.name }}</span>
      </a>
    </span>
  </div>
</template>
